/**
 * Helper functions for leave calculations and management
 */

/**
 * Calculate the number of days between two dates (inclusive)
 * @param {string|Date} startDate - Start date
 * @param {string|Date} endDate - End date
 * @returns {number} Number of days (inclusive)
 */
export const calculateLeaveDays = (startDate, endDate) => {
  if (!startDate || !endDate) return 1;
  
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return 1;
  
  const diffTime = Math.abs(end - start);
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
};

/**
 * Calculate leave balance for a staff member
 * @param {Object} staffMember - Staff member object
 * @param {Array} leaveRequests - Array of leave requests for the staff member
 * @param {Object} leaveSettings - Leave settings from admin
 * @returns {Object} Leave balance information
 */
export const calculateLeaveBalance = (staffMember, leaveRequests, leaveSettings = {}) => {
  const currentYear = new Date().getFullYear();
  const leaveYearStart = leaveSettings.leaveYearStartMonth || 1;
  const leaveYearStartDay = leaveSettings.leaveYearStartDay || 1;
  
  // Calculate leave year boundaries
  const leaveYearStartDate = new Date(currentYear, leaveYearStart - 1, leaveYearStartDay);
  const leaveYearEndDate = new Date(currentYear + 1, leaveYearStart - 1, leaveYearStartDay - 1);
  
  // Filter leave requests for current leave year
  const currentYearLeaveRequests = leaveRequests.filter(request => {
    const requestDate = new Date(request.startDate);
    return requestDate >= leaveYearStartDate && requestDate <= leaveYearEndDate;
  });
  
  // Calculate used days by type
  const usedDays = {
    leave: 0,
    sick: 0,
    maternity: 0,
    personal: 0,
    emergency: 0,
    training: 0,
    remote: 0
  };
  
  currentYearLeaveRequests.forEach(request => {
    if (request.status === 'approved') {
      const days = request.daysCount || calculateLeaveDays(request.startDate, request.endDate);
      usedDays[request.type] = (usedDays[request.type] || 0) + days;
    }
  });
  
  // Use clinic-specific allowances from settings, with fallback to staff-specific or default values
  const entitlements = {
    leave: staffMember.leaveEntitlement || leaveSettings.annualLeaveAllowance || 25,
    sick: staffMember.sickLeaveEntitlement || leaveSettings.sickLeaveAllowance || 10,
    maternity: staffMember.maternityLeaveEntitlement || leaveSettings.maternityLeaveAllowance || 90,
    personal: staffMember.personalLeaveEntitlement || leaveSettings.personalLeaveAllowance || 5,
    emergency: staffMember.emergencyLeaveEntitlement || leaveSettings.emergencyLeaveAllowance || 3,
    training: staffMember.trainingDaysEntitlement || 5,
    remote: staffMember.remoteDaysEntitlement || 52 // 1 day per week
  };
  
  // Calculate remaining days
  const remainingDays = {};
  Object.keys(entitlements).forEach(type => {
    remainingDays[type] = Math.max(0, entitlements[type] - usedDays[type]);
  });
  
  return {
    leaveYear: {
      start: leaveYearStartDate,
      end: leaveYearEndDate
    },
    entitlements,
    usedDays,
    remainingDays,
    totalUsed: Object.values(usedDays).reduce((sum, days) => sum + days, 0)
  };
};

/**
 * Determine if a leave request requires approval based on approval matrix
 * @param {string} eventType - Type of event (leave, sick, training, etc.)
 * @param {number} daysCount - Number of days requested
 * @param {Object} staffMember - Staff member making the request
 * @returns {Object} Approval decision
 */
export const getApprovalDecision = (eventType, daysCount, staffMember = {}) => {
  const decisions = {
    leave: {
      status: 'pending',
      autoApproved: false,
      reason: 'Annual leave always requires approval'
    },
    maternity: {
      status: 'pending',
      autoApproved: false,
      reason: 'Maternity/Paternity leave always requires approval'
    },
    personal: {
      status: 'pending',
      autoApproved: false,
      reason: 'Personal leave requires approval'
    },
    emergency: {
      status: daysCount <= 1 ? 'approved' : 'pending',
      autoApproved: daysCount <= 1,
      reason: daysCount <= 1 ? 'Single day emergency leave auto-approved' : 'Multi-day emergency leave requires approval'
    },
    sick: {
      status: daysCount === 1 ? 'approved' : 'pending',
      autoApproved: daysCount === 1,
      reason: daysCount === 1 ? 'Single day sick leave auto-approved' : 'Multi-day sick leave requires approval'
    },
    remote: {
      status: 'approved',
      autoApproved: true,
      reason: 'Remote work auto-approved'
    },
    training: {
      status: 'approved',
      autoApproved: true,
      reason: 'Training auto-approved'
    },
    meeting: {
      status: 'confirmed',
      autoApproved: true,
      reason: 'Meetings are automatically confirmed'
    },
    appointment: {
      status: 'confirmed',
      autoApproved: true,
      reason: 'Appointments are automatically confirmed'
    }
  };
  
  return decisions[eventType] || {
    status: 'confirmed',
    autoApproved: true,
    reason: 'Default auto-approval'
  };
};

/**
 * Format leave type for display
 * @param {string} type - Leave type
 * @returns {string} Formatted display name
 */
export const formatLeaveType = (type) => {
  const typeMap = {
    leave: 'Annual Leave',
    sick: 'Sick Leave',
    maternity: 'Maternity/Paternity Leave',
    personal: 'Personal Leave',
    emergency: 'Emergency Leave',
    remote: 'Remote Work',
    training: 'Training',
    meeting: 'Meeting',
    appointment: 'Appointment'
  };
  
  return typeMap[type] || type.charAt(0).toUpperCase() + type.slice(1);
};

/**
 * Get status color for leave requests
 * @param {string} status - Leave request status
 * @returns {string} Tailwind color class
 */
export const getStatusColor = (status) => {
  const colorMap = {
    pending: 'text-yellow-600 bg-yellow-100',
    approved: 'text-green-600 bg-green-100',
    declined: 'text-red-600 bg-red-100',
    confirmed: 'text-blue-600 bg-blue-100',
    cancelled: 'text-gray-600 bg-gray-100'
  };
  
  return colorMap[status] || 'text-gray-600 bg-gray-100';
};

/**
 * Check if a leave request can be edited
 * @param {Object} leaveRequest - Leave request object
 * @returns {boolean} Whether the request can be edited
 */
export const canEditLeaveRequest = (leaveRequest) => {
  return ['pending', 'declined'].includes(leaveRequest.status);
};

/**
 * Check if a leave request can be cancelled
 * @param {Object} leaveRequest - Leave request object
 * @returns {boolean} Whether the request can be cancelled
 */
export const canCancelLeaveRequest = (leaveRequest) => {
  return ['pending', 'approved'].includes(leaveRequest.status);
};

/**
 * Calculate leave year boundaries based on settings
 * @param {Object} leaveSettings - Leave settings from admin
 * @param {number} year - Year to calculate for (defaults to current year)
 * @returns {Object} Leave year start and end dates
 */
export const getLeaveYearBoundaries = (leaveSettings = {}, year = null) => {
  const targetYear = year || new Date().getFullYear();
  const startMonth = (leaveSettings.leaveYearStartMonth || 1) - 1; // 0-indexed
  const startDay = leaveSettings.leaveYearStartDay || 1;
  
  const start = new Date(targetYear, startMonth, startDay);
  const end = new Date(targetYear + 1, startMonth, startDay - 1);
  
  return { start, end };
};
